import { Component } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaEraser, FaEye, FaSearch, FaRobot } from "react-icons/fa";
import AsyncSelect from "react-select/async";
import Paginacao from "../../common/pagination";
import programasService from "../../api/programas/programasService";
import { getCurrentUser, estados } from "../../common/utils";
import api from "../../services/api";

const PERFIL_LABELS = {
  medico: "Médico",
  paciente: "Paciente",
};

const NOTA_LABELS = {
  1: "😞 Péssimo",
  2: "😕 Ruim",
  3: "😐 Regular",
  4: "🙂 Bom",
  5: "😄 Ótimo",
};

function formatarData(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PerfilBadge({ perfil }) {
  const variant = perfil === "medico" ? "primary" : "success";
  return (
    <Badge bg={variant} className="text-capitalize">
      {PERFIL_LABELS[perfil] || perfil || "-"}
    </Badge>
  );
}

function NotaBadge({ nota }) {
  if (!nota) return <span className="text-muted">-</span>;
  const variant =
    nota >= 4 ? "success" : nota === 3 ? "warning" : "danger";
  return (
    <Badge bg={variant}>
      {nota}/5
    </Badge>
  );
}

class HistoricoChatbot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: "",

      // Filtros
      filtro_programa: null,
      filtro_perfil: "",
      filtro_uf: "",
      filtro_data_inicio: "",
      filtro_data_fim: "",
      filtro_whatsapp_clicado: "",

      // Dados
      registros: [],
      dadosPaginacao: null,
      page_numb: 1,

      // Modal detalhe
      exibirDetalhe: false,
      detalheLoading: false,
      detalheItem: null,
    };
  }

  componentDidMount() {
    this.carregarDados();
  }

  carregarDados = (page = 1) => {
    const {
      filtro_programa,
      filtro_perfil,
      filtro_uf,
      filtro_data_inicio,
      filtro_data_fim,
      filtro_whatsapp_clicado,
    } = this.state;

    const dadosUsuario = getCurrentUser();
    const params = { page };

    // Sem o tipo do usuário o backend cai nos programas de usuarios_programas; com ele,
    // resolve o escopo pelas carteiras do operador / clientes do gestor, como nas demais telas
    if (!dadosUsuario?.is_superuser && dadosUsuario?.tipo_usuario) {
      params.tipo_usuario = dadosUsuario.tipo_usuario;
    }

    // Superuser pode filtrar por programa; operador recebe escopo automático do backend
    if (filtro_programa || dadosUsuario['ambiente_programa_id']) {
      params.programa_id = dadosUsuario['ambiente_programa_id'] || filtro_programa.value;
    }

    if (filtro_perfil) params.perfil = filtro_perfil;
    if (filtro_uf) params.uf = filtro_uf;
    if (filtro_data_inicio) params.criado_apos = filtro_data_inicio;
    if (filtro_data_fim) params.criado_antes = filtro_data_fim;
    if (filtro_whatsapp_clicado) params.whatsapp_clicado = filtro_whatsapp_clicado;

    this.setState({ loading: true, error: "" });

    api
      .get("ceaf/chatbot/auditoria/historico/", { params })
      .then((response) => {
        const results = response.data.results ?? response.data ?? [];
        const total = response.data.count ?? results.length;
        this.setState({
          registros: results,
          dadosPaginacao: {
            count: total,
            next: response.data.next ?? null,
            previous: response.data.previous ?? null,
          },
          page_numb: page,
          loading: false,
        });
      })
      .catch((err) => {
        const msg =
          err?.response?.status === 403
            ? "Permissão negada."
            : "Erro ao carregar histórico do chatbot.";
        this.setState({ loading: false, error: msg });
      });
  };

  abrirDetalhe = (item) => {
    this.setState({ exibirDetalhe: true, detalheLoading: true, detalheItem: item });

    api
      .get(`ceaf/chatbot/auditoria/historico/${item.id}/`)
      .then((response) => {
        this.setState({ detalheItem: response.data, detalheLoading: false });
      })
      .catch(() => {
        this.setState({ detalheLoading: false });
      });
  };

  fecharDetalhe = () => {
    this.setState({ exibirDetalhe: false, detalheItem: null });
  };

  limparFiltros = () => {
    this.setState(
      {
        filtro_programa: null,
        filtro_perfil: "",
        filtro_uf: "",
        filtro_data_inicio: "",
        filtro_data_fim: "",
        filtro_whatsapp_clicado: "",
      },
      () => this.carregarDados(1)
    );
  };

  handleFiltrar = (e) => {
    e.preventDefault();
    this.carregarDados(1);
  };

  render() {
    const dadosUsuario = getCurrentUser();
    const isSuperuser = Boolean(dadosUsuario?.is_superuser);
    const isOperador = dadosUsuario?.tipo_usuario === 'operador';
    const isGestor = dadosUsuario?.tipo_usuario === 'gestor';

    // Somente superuser ou operador podem acessar
    if (!isSuperuser && !isOperador && !isGestor) {
      return <Navigate to="/sem-permissao" />;
    }

    const {
      loading,
      error,
      registros,
      dadosPaginacao,
      page_numb,
      filtro_programa,
      filtro_perfil,
      filtro_uf,
      filtro_data_inicio,
      filtro_data_fim,
      filtro_whatsapp_clicado,
      exibirDetalhe,
      detalheLoading,
      detalheItem,
    } = this.state;

    const ufsDisponiveis = Object.entries(estados)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([uf, nome]) => ({ value: uf, label: `${nome} (${uf})` }));

    return (
      <div>
        <Row className="align-items-center mb-2">
          <Col>
            <h4 className="page-title">
              <FaRobot className="me-2" />
              Histórico do Chatbot CEAF
            </h4>
          </Col>
        </Row>

        {/* Filtros */}
        <Card className="cardDestaque">
          <Card.Body>
            <h5 className="mb-3">Filtros</h5>
            <Form onSubmit={this.handleFiltrar}>
              <Row className="align-items-end g-3">
                {/* Filtro de programa: visível apenas para superuser */}
                {isSuperuser && (
                  <Col sm={12} md={4}>
                    <Form.Group>
                      <Form.Label>Programa</Form.Label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={programasService.loadProgramas}
                        placeholder="Todos"
                        noOptionsMessage={() => "Nenhum programa encontrado"}
                        isClearable
                        value={filtro_programa}
                        onChange={(opt) => this.setState({ filtro_programa: opt })}
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col sm={6} md={2}>
                  <Form.Group>
                    <Form.Label>Perfil</Form.Label>
                    <Form.Select
                      value={filtro_perfil}
                      onChange={(e) => this.setState({ filtro_perfil: e.target.value })}
                    >
                      <option value="">Todos</option>
                      <option value="medico">Médico</option>
                      <option value="paciente">Paciente</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col sm={6} md={2}>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      value={filtro_uf}
                      onChange={(e) => this.setState({ filtro_uf: e.target.value })}
                    >
                      <option value="">Todos</option>
                      {ufsDisponiveis.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col sm={6} md={2}>
                  <Form.Group>
                    <Form.Label>WhatsApp</Form.Label>
                    <Form.Select
                      value={filtro_whatsapp_clicado}
                      onChange={(e) => this.setState({ filtro_whatsapp_clicado: e.target.value })}
                    >
                      <option value="">Todos</option>
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col sm={6} md={2}>
                  <Form.Group>
                    <Form.Label>Data início</Form.Label>
                    <Form.Control
                      type="date"
                      value={filtro_data_inicio}
                      onChange={(e) => this.setState({ filtro_data_inicio: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col sm={6} md={2}>
                  <Form.Group>
                    <Form.Label>Data fim</Form.Label>
                    <Form.Control
                      type="date"
                      value={filtro_data_fim}
                      onChange={(e) => this.setState({ filtro_data_fim: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  <Button type="submit" variant="success" size="sm" className="me-2">
                    <FaSearch className="me-1" /> Filtrar
                  </Button>
                  <Button type="button" variant="light" size="sm" onClick={this.limparFiltros}>
                    <FaEraser className="me-1" /> Limpar
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        <p className="mt-3 mb-2 text-muted">{dadosPaginacao?.count ?? registros.length} registro(s) encontrado(s)</p>

        {/* Tabela */}
        <Card className="cardDestaque">
          <Card.Body className="pt-0">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Carregando...
              </div>
            ) : error ? (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            ) : registros.length === 0 ? (
              <Alert variant="light" className="mt-3 mb-0">
                Nenhum registro encontrado para os filtros selecionados.
              </Alert>
            ) : (
              <>
                <Table responsive className="custom-table">
                  <thead>
                    <tr>
                      <th>Data / Hora</th>
                      <th>Programa</th>
                      <th>Perfil</th>
                      <th>Estado (UF)</th>
                      <th>Medicamento</th>
                      <th>WhatsApp</th>
                      <th>IP</th>
                      <th>Avaliação</th>
                      <th className="text-end">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registros.map((item) => (
                      <tr key={item.id}>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {formatarData(item.criado_em)}
                        </td>
                        <td>{item.programa_nome || "-"}</td>
                        <td>
                          <PerfilBadge perfil={item.perfil} />
                        </td>
                        <td>
                          {item.uf
                            ? `${estados[item.uf] ? `${estados[item.uf]} (${item.uf})` : item.uf}`
                            : "-"}
                        </td>
                        <td className="text-capitalize">
                          {item.medicamento || "-"}
                        </td>
                        <td>
                          {item.whatsapp_clicado ? (
                            <Badge bg="success">Sim</Badge>
                          ) : (
                            <Badge bg="secondary">Não</Badge>
                          )}
                        </td>
                        <td>
                          <code style={{ fontSize: "0.8em" }}>
                            {item.ip_origem || "-"}
                          </code>
                        </td>
                        <td>
                          <NotaBadge nota={item.nota_utilidade} />
                        </td>
                        <td className="text-end">
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => this.abrirDetalhe(item)}
                          >
                            <FaEye className="me-1" /> Ver
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Paginacao
                  dados={dadosPaginacao}
                  page_numb={page_numb}
                  loading={loading}
                  _onClickPagina={this.carregarDados}
                />
              </>
            )}
          </Card.Body>
        </Card>

        {/* Modal de detalhe */}
        <Modal
          show={exibirDetalhe}
          onHide={this.fecharDetalhe}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Sessão #{detalheItem?.id} — {formatarData(detalheItem?.criado_em)}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detalheLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Carregando detalhes...
              </div>
            ) : detalheItem ? (
              <DetalheAuditoria item={detalheItem} />
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={this.fecharDetalhe}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

/* ── Componente de detalhe da sessão ─────────────────────────────────────── */

function DetalheAuditoria({ item }) {
  const historico = Array.isArray(item.historico_json) ? item.historico_json : [];

  return (
    <div>
      {/* Cabeçalho resumido */}
      <Card className="mb-3 border-0 bg-light">
        <Card.Body>
          <Row className="g-2">
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Programa</small>
              <strong>{item.programa_nome || "-"}</strong>
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Perfil</small>
              <PerfilBadge perfil={item.perfil} />
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Estado consultado</small>
              <strong>{item.uf || "-"}</strong>
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Medicamento</small>
              <strong className="text-capitalize">{item.medicamento || "-"}</strong>
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Tipo de solicitação</small>
              <strong>{item.tipo_solicitacao_display || item.tipo_solicitacao || "-"}</strong>
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">Avaliação final</small>
              {item.nota_utilidade ? (
                <strong>{NOTA_LABELS[item.nota_utilidade] || `${item.nota_utilidade}/5`}</strong>
              ) : (
                <span className="text-muted">-</span>
              )}
            </Col>
            <Col xs={6} md={4}>
              <small className="text-muted d-block">IP de origem</small>
              <code style={{ fontSize: "0.85em" }}>{item.ip_origem || "-"}</code>
            </Col>
            <Col xs={12} md={8}>
              <small className="text-muted d-block">User-Agent</small>
              <span style={{ fontSize: "0.8em", wordBreak: "break-all" }}>
                {item.user_agent || "-"}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Histórico de eventos */}
      <h6 className="fw-bold mb-3">Histórico de interações</h6>
      {historico.length === 0 ? (
        <p className="text-muted">Nenhuma interação registrada nesta sessão.</p>
      ) : (
        <div className="d-flex flex-column gap-2">
          {historico.map((evento, idx) => (
            <EventoCard key={idx} evento={evento} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventoCard({ evento }) {
  const tipo = evento.tipo || evento.evento || "evento";
  const ts = evento.ts || evento.timestamp;

  const variantMap = {
    inicio_sessao: "info",
    selecao_uf: "primary",
    selecao_medicamento: "secondary",
    selecao_tipo: "secondary",
    step_avancado: "light",
    navegador_ia: "warning",
    feedback_satisfacao: "success",
  };
  const variant = variantMap[tipo] || "light";

  return (
    <Card className={`border-0 border-start border-3 border-${variant} ps-0`} style={{ background: "#fafafa" }}>
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <Badge bg={variant === "light" ? "secondary" : variant} style={{ fontSize: "0.75em" }}>
            {tipo.replace(/_/g, " ")}
          </Badge>
          {ts && (
            <small className="text-muted">{formatarData(ts)}</small>
          )}
        </div>

        {/* Pergunta/resposta IA */}
        {evento.pergunta && (
          <div className="mt-1">
            <small className="text-muted fw-bold">Pergunta:</small>
            <p className="mb-1 mt-1" style={{ fontSize: "0.9em" }}>
              {evento.pergunta}
            </p>
          </div>
        )}
        {evento.resposta && (
          <div>
            <small className="text-muted fw-bold">Resposta IA:</small>
            <p className="mb-0 mt-1" style={{ fontSize: "0.9em", whiteSpace: "pre-wrap" }}>
              {evento.resposta}
            </p>
          </div>
        )}

        {/* Campos genéricos */}
        {Object.entries(evento)
          .filter(([k]) => !["tipo", "evento", "ts", "timestamp", "pergunta", "resposta"].includes(k))
          .map(([k, v]) => (
            <div key={k}>
              <small className="text-muted">{k}: </small>
              <small>{String(v)}</small>
            </div>
          ))}
      </Card.Body>
    </Card>
  );
}

export default HistoricoChatbot;
